import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import { db } from "../firebase";
import {
  collection, addDoc, query, where, onSnapshot,
  deleteDoc, doc, updateDoc, getDocs,
} from "firebase/firestore";
import Seite from "../components/Seite";

// ── Rich-Text-Editor ──────────────────────────────────────────────────────────
function RichTextEditor({ initialWert, onChange }) {
  const editorRef = useRef(null);
  const initialisiert = useRef(false);

  useEffect(() => {
    if (!initialisiert.current && editorRef.current && initialWert !== undefined) {
      const istHtml = /<[a-z][\s\S]*>/i.test(initialWert || "");
      const roh = istHtml ? initialWert : (initialWert || "").replace(/\n/g, "<br>");
      // initialWert kann Rohtext externer Mails enthalten – ungesäubert ist das gespeichertes XSS.
      editorRef.current.innerHTML = DOMPurify.sanitize(roh);
      initialisiert.current = true;
    }
  }, [initialWert]);

  const format = (cmd, wert) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, wert || null);
  };

  const TOOLBAR = [
    { label: <b>F</b>,  title: "Fett",              cmd: "bold" },
    { label: <i>K</i>,  title: "Kursiv",             cmd: "italic" },
    { label: <u>U</u>,  title: "Unterstrichen",      cmd: "underline" },
    { label: "•",       title: "Liste",              cmd: "insertUnorderedList" },
    { label: "1.",      title: "Nummerierte Liste",  cmd: "insertOrderedList" },
    { label: "H",       title: "Überschrift",        cmd: "formatBlock", wert: "H3" },
    { label: "¶",       title: "Normal",             cmd: "formatBlock", wert: "P" },
    { label: "—",       title: "Trennlinie",         cmd: "insertHorizontalRule" },
  ];

  return (
    <div style={rt.wrapper}>
      <div style={rt.toolbar}>
        {TOOLBAR.map((btn, i) => (
          <button
            key={i}
            title={btn.title}
            style={rt.toolBtn}
            onMouseDown={(e) => { e.preventDefault(); format(btn.cmd, btn.wert); }}
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        style={rt.editor}
      />
    </div>
  );
}

const rt = {
  wrapper: { display: "flex", flexDirection: "column", border: "1px solid #ddd", borderRadius: "6px", overflow: "hidden", marginTop: "0.7rem" },
  toolbar: { display: "flex", flexWrap: "wrap", gap: "2px", padding: "4px 6px", backgroundColor: "#f8f9fa", borderBottom: "1px solid #ddd" },
  toolBtn: { minWidth: "28px", height: "26px", padding: "0 5px", border: "1px solid #ccc", borderRadius: "3px", backgroundColor: "white", cursor: "pointer", fontSize: "0.85rem", fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" },
  editor: { minHeight: "180px", maxHeight: "420px", overflowY: "auto", padding: "0.8rem", fontSize: "0.92rem", outline: "none", lineHeight: 1.6, fontFamily: "sans-serif" },
};

// ── Hilfskomponenten ──────────────────────────────────────────────────────────
const KATEGORIEN = [
  { id: "alle",      label: "Alle" },
  { id: "beruflich", label: "💼 Beruflich" },
  { id: "privat",    label: "🏠 Privat" },
];

function ProjektAuswahl({ user, onWaehlen }) {
  const [projekte, setProjekte] = useState([]);
  useEffect(() => {
    (async () => {
      const snap = await getDocs(query(collection(db, "projekte"), where("uid", "==", user.uid)));
      setProjekte(snap.docs.map((d) => ({ id: d.id, titel: d.data().titel })));
    })();
  }, [user.uid]);
  return (
    <select style={styles.select} onChange={(e) => e.target.value && onWaehlen(e.target.value)} defaultValue="">
      <option value="" disabled>Projekt wählen...</option>
      <option value="__neu__">+ Neues Projekt erstellen</option>
      {projekte.map((p) => <option key={p.id} value={p.id}>{p.titel}</option>)}
    </select>
  );
}

// ── Einzelne Mail-Karte (eigene Komponente für Editor-State) ──────────────────
function MailKarte({ mail, user, onKategorieWechsel, onLoeschen, onNotiz, onAufgabe, onProjekt }) {
  const [offen, setOffen] = useState(false);
  const [editInhalt, setEditInhalt] = useState(mail.text || "");
  const [betreffEdit, setBetreffEdit] = useState(false);
  const [betreffText, setBetreffText] = useState(mail.betreff || "");
  const [verschiebenOffen, setVerschiebenOffen] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);

  const kat = mail.kategorie || "beruflich";

  const speichern = async () => {
    await updateDoc(doc(db, "mailablage", mail.id), {
      text: editInhalt,
      betreff: betreffText,
    });
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 2000);
  };

  const betreffSpeichern = async () => {
    await updateDoc(doc(db, "mailablage", mail.id), { betreff: betreffText });
    setBetreffEdit(false);
  };

  const formatDatum = (iso) => iso
    ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
    : "";

  return (
    <div style={styles.karte}>
      {/* Kopfzeile */}
      <div style={styles.kopf}>
        <button
          style={{ ...styles.katBadge(kat), border: "none", cursor: "pointer" }}
          title="Kategorie wechseln"
          onClick={() => onKategorieWechsel(mail.id, kat)}
        >
          {kat === "beruflich" ? "💼" : "🏠"}
        </button>

        <div style={styles.kopfMitte} onClick={() => !betreffEdit && setOffen(!offen)}>
          {betreffEdit ? (
            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
              <input
                style={styles.betreffInput}
                value={betreffText}
                onChange={(e) => setBetreffText(e.target.value)}
                autoFocus
              />
              <button style={styles.speichernKlein} onClick={betreffSpeichern}>💾</button>
              <button style={styles.abbrechenKlein} onClick={() => { setBetreffEdit(false); setBetreffText(mail.betreff || ""); }}>✕</button>
            </div>
          ) : (
            <>
              <span style={styles.betreff}>{mail.betreff || "(Ohne Betreff)"}</span>
              <span style={styles.meta}>
                {mail.absender && <span>{mail.absender} · </span>}
                {formatDatum(mail.datum)}
              </span>
            </>
          )}
        </div>

        <button style={styles.iconBtn} title="Betreff bearbeiten" onClick={() => { setBetreffEdit(true); setBetreffText(mail.betreff || ""); }}>✏️</button>
        <button style={styles.iconBtn} title="Löschen" onClick={() => onLoeschen(mail.id)}>🗑️</button>
      </div>

      {/* Detail-Bereich */}
      {offen && (
        <div style={styles.detail}>
          {/* Aktionsleiste */}
          <div style={styles.aktionen}>
            <button style={styles.aktionBtn} onClick={() => onNotiz({ ...mail, text: editInhalt, betreff: betreffText })}>📝 Notiz</button>
            <button style={styles.aktionBtn} onClick={() => onAufgabe({ ...mail, text: editInhalt, betreff: betreffText })}>✅ Aufgabe</button>
            <button
              style={{ ...styles.aktionBtn, backgroundColor: verschiebenOffen ? "#e8f0fe" : undefined }}
              onClick={() => setVerschiebenOffen(!verschiebenOffen)}
            >
              📁 Projekt
            </button>
            <button
              style={{ ...styles.aktionBtn, backgroundColor: gespeichert ? "#e6f4ea" : "#1a73e8", color: gespeichert ? "#34a853" : "white", borderColor: gespeichert ? "#34a853" : "#1a73e8" }}
              onClick={speichern}
            >
              {gespeichert ? "✅ Gespeichert" : "💾 Speichern"}
            </button>
          </div>

          {verschiebenOffen && (
            <div style={{ marginBottom: "0.6rem" }}>
              <ProjektAuswahl user={user} onWaehlen={(pid) => onProjekt({ ...mail, text: editInhalt, betreff: betreffText }, pid)} />
            </div>
          )}

          {/* Rich-Text-Editor */}
          <RichTextEditor
            initialWert={mail.text || ""}
            onChange={setEditInhalt}
          />
        </div>
      )}
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────────
export default function MailAblage({ user }) {
  const [mails, setMails] = useState([]);
  const [filter, setFilter] = useState("alle");

  useEffect(() => {
    const q = query(collection(db, "mailablage"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.erstellt?.toMillis?.() || 0) - (a.erstellt?.toMillis?.() || 0));
      setMails(data);
    });
  }, [user.uid]);

  const kategorieWechseln = (id, aktuelle) =>
    updateDoc(doc(db, "mailablage", id), { kategorie: aktuelle === "beruflich" ? "privat" : "beruflich" });

  const loeschen = (id) => deleteDoc(doc(db, "mailablage", id));

  const alsNotizSpeichern = async (mail) => {
    await addDoc(collection(db, "notizen"), {
      text: mail.betreff || "(Ohne Betreff)",
      uid: user.uid,
      notiz: { text: mail.text || "" },
      kategorie: mail.kategorie || "beruflich",
      erstellt: new Date(),
    });
    await loeschen(mail.id);
  };

  const alsAufgabeSpeichern = async (mail) => {
    await addDoc(collection(db, "todo"), {
      titel: mail.betreff || "(Ohne Betreff)",
      kategorie: mail.kategorie || "beruflich",
      punkte: [{ text: (mail.text || "").replace(/<[^>]+>/g, " ").slice(0, 300).trim(), erledigt: false, id: Date.now().toString() }],
      uid: user.uid,
      reihenfolge: Date.now(),
      erstellt: new Date(),
    });
    await loeschen(mail.id);
  };

  const zuProjektVerschieben = async (mail, projektId) => {
    const neuerUnterpunkt = {
      text: mail.betreff || "(Ohne Betreff)",
      erledigt: false,
      id: Date.now().toString(),
      prioritaet: "mittel",
      erledigtBis: "",
      notiz: { text: mail.text || "" },
    };
    if (projektId === "__neu__") {
      await addDoc(collection(db, "projekte"), {
        titel: mail.betreff || "(Ohne Betreff)",
        unterpunkte: [neuerUnterpunkt],
        uid: user.uid,
        reihenfolge: Date.now(),
        erstellt: new Date(),
      });
    } else {
      const snap = await getDocs(query(collection(db, "projekte"), where("uid", "==", user.uid)));
      const projektDoc = snap.docs.find((d) => d.id === projektId);
      if (projektDoc) {
        const bisherige = projektDoc.data().unterpunkte || [];
        await updateDoc(doc(db, "projekte", projektId), { unterpunkte: [...bisherige, neuerUnterpunkt] });
      }
    }
    await loeschen(mail.id);
  };

  const gefiltertesMails = filter === "alle"
    ? mails
    : mails.filter((m) => (m.kategorie || "beruflich") === filter);

  return (
    <Seite titel="Mails zum Bearbeiten">
      <div style={styles.filterLeiste}>
        {KATEGORIEN.map((k) => (
          <button
            key={k.id}
            style={{ ...styles.filterBtn, ...(filter === k.id ? styles.filterBtnAktiv : {}) }}
            onClick={() => setFilter(k.id)}
          >
            {k.label}
          </button>
        ))}
      </div>

      {gefiltertesMails.length === 0 && <p style={styles.leer}>Keine Mails in dieser Kategorie.</p>}

      {gefiltertesMails.map((mail) => (
        <MailKarte
          key={mail.id}
          mail={mail}
          user={user}
          onKategorieWechsel={kategorieWechseln}
          onLoeschen={loeschen}
          onNotiz={alsNotizSpeichern}
          onAufgabe={alsAufgabeSpeichern}
          onProjekt={zuProjektVerschieben}
        />
      ))}
    </Seite>
  );
}

const styles = {
  filterLeiste: { display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" },
  filterBtn: { padding: "0.4rem 0.9rem", border: "1px solid #ddd", borderRadius: "20px", cursor: "pointer", fontSize: "0.85rem", backgroundColor: "white", color: "#555" },
  filterBtnAktiv: { backgroundColor: "#1a73e8", color: "white", borderColor: "#1a73e8" },
  leer: { color: "#999", textAlign: "center", marginTop: "2rem" },
  karte: { backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "0.5rem", overflow: "hidden" },
  kopf: { display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.7rem 0.9rem" },
  kopfMitte: { flex: 1, cursor: "pointer", display: "flex", flexDirection: "column", gap: "0.1rem" },
  betreff: { fontWeight: "600", color: "#1a3558", fontSize: "0.95rem" },
  betreffInput: { flex: 1, padding: "0.3rem 0.5rem", fontSize: "0.92rem", border: "1px solid #ddd", borderRadius: "4px" },
  meta: { fontSize: "0.78rem", color: "#888" },
  detail: { borderTop: "1px solid #f0f0f0", padding: "0.7rem 0.9rem 0.9rem" },
  aktionen: { display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem" },
  aktionBtn: { padding: "0.35rem 0.7rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontSize: "0.82rem" },
  select: { padding: "0.4rem 0.6rem", border: "1px solid #ddd", borderRadius: "4px", fontSize: "0.85rem", width: "100%" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "1rem" },
  speichernKlein: { padding: "0.2rem 0.5rem", backgroundColor: "#34a853", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.85rem" },
  abbrechenKlein: { padding: "0.2rem 0.5rem", backgroundColor: "#ea4335", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.85rem" },
  katBadge: (kat) => ({ fontSize: "0.9rem", padding: "0.15rem 0.3rem", borderRadius: "4px", backgroundColor: kat === "beruflich" ? "#e8f0fe" : "#fce8f3", flexShrink: 0 }),
};
